/**
 * The handle every consumer gets, and deliberately the smallest one that works.
 *
 * Split from `MotionProvider.tsx` so the module exports only values — a file that exports
 * both a context and a component makes Vite's Fast Refresh give up and reload the page,
 * which is exactly the wrong trade in a folder about smoothness.
 */
import { createContext, useContext } from 'react';
import type { Lenis } from './lenisRuntime';
import type { MotionRuntime } from './runtime';

export interface MotionApi {
  /** Scroll-driven and entrance animations are wanted here. */
  animate: boolean;
  /** Lenis owns the page scroll. */
  smooth: boolean;
  /** GSAP, once it has loaded. `null` until then, and forever where `animate` is false. */
  runtime: MotionRuntime | null;
  /**
   * The live Lenis instance, or `null`.
   *
   * A getter rather than a value: the instance appears one microtask after the provider
   * mounts, and handing it out as a field would mean re-rendering every consumer at that
   * moment to deliver something almost none of them read.
   */
  getLenis: () => Lenis | null;
}

const IDLE: MotionApi = {
  animate: false,
  smooth: false,
  runtime: null,
  getLenis: () => null,
};

export const MotionContext = createContext<MotionApi>(IDLE);

/**
 * Reading the context outside the provider is not an error.
 *
 * The default says "no motion here", which is the truthful answer for a tree rendered
 * without the provider — a test, or a component mounted in isolation. Throwing would mean
 * every consumer needed a provider even to render statically, and every hook in this
 * folder is written so that no motion is a supported outcome rather than a failure.
 */
export function useMotion(): MotionApi {
  return useContext(MotionContext);
}
